// 編輯器進入點：載入字型、建立狀態與各元件、綁定選單與快捷鍵。
import { createFontSet, serializeOutline, STRUCTURES, THEME_NAMES, type Diagnostic, type FontSet, type MindMap, type Structure } from '@sind/core';
import { EditorState, blankMap } from './state.js';
import { Canvas } from './canvas.js';
import { Commands } from './commands.js';
import { Inspector } from './panel.js';
import { Help } from './help.js';
import { AutoSaver, localDraft } from './storage.js';
import { importFile, download, baseName, exportSvgText, exportPng, exportMarkdown, exportXmindBlob, claudeBrief, share, outlineToMap } from './files.js';
import { t, lang, applyI18n, type I18nKey } from './i18n.js';
import { AccountUI } from './account.js';
import { CloudStorage, me, createMap, loadMap, shareCloudMap, errorMessage } from './cloud.js';

const CURRENT_CLOUD_KEY = 'sind-current-cloud';
const storageGet = (key: string) => { try { return localStorage.getItem(key); } catch { return null; } };
const storageSet = (key: string, value: string | null) => {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* 忽略 */ }
};

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const isMac = /Mac|iPhone|iPad/.test(navigator.platform);

// 下載字型（中文字型約 4 MB，瀏覽器會快取）。
async function loadFonts(): Promise<FontSet> {
  const fetchBytes = async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
    return new Uint8Array(await response.arrayBuffer());
  };
  const [latin, cjk] = await Promise.all([fetchBytes('/fonts/excalifont.woff2'), fetchBytes('/fonts/xiaolai.woff2')]);
  return createFontSet({ latin, cjk });
}

let toastTimer = 0;
function toast(message: string): void {
  const element = $('toast');
  element.textContent = message;
  element.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => element.classList.remove('show'), 2400);
}

function showDiagnostics(list: HTMLElement, diagnostics: Diagnostic[]): void {
  list.replaceChildren(...diagnostics.map(item => Object.assign(document.createElement('li'), {
    className: item.severity, textContent: `${item.line ? `L${item.line} ` : ''}${item.message}`,
  })));
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const area = Object.assign(document.createElement('textarea'), { value: text });
    document.body.append(area);
    area.select();
    document.execCommand('copy');
    area.remove();
  }
  toast(t('toast.copied'));
}

// 初始內容：?map=<分享 id> > 瀏覽器草稿 > 空白圖。
async function initialMap(): Promise<MindMap> {
  const shared = new URLSearchParams(location.search).get('map');
  if (shared && /^[a-z0-9-]{1,64}$/.test(shared)) {
    try {
      const response = await fetch(`/m/${shared}.md`);
      if (response.ok) return outlineToMap(await response.text()).map;
    } catch { /* 改用草稿 */ }
  }
  return localDraft.load() ?? blankMap(t('default.central'));
}

async function main(): Promise<void> {
  applyI18n();
  if (isMac) document.querySelectorAll('.mod-key').forEach(element => { element.textContent = element.textContent!.replace('Ctrl+', '⌘'); });

  let fonts: FontSet;
  try {
    fonts = await loadFonts();
  } catch (error) {
    console.error(error);
    $('loading').textContent = t('app.loadFailed');
    return;
  }

  const state = new EditorState(await initialMap());
  const viewport = $('viewport');
  const outlineText = $<HTMLTextAreaElement>('outline-text');
  let commands!: Commands;
  let inspector!: Inspector;

  const canvas = new Canvas(viewport, state, fonts, {
    onEditCommit(id, title, then) {
      commands.rename(id, title);
      if (then === 'child') commands.addChild(id);
    },
    onMove: (id, target, position) => commands.move(id, target, position),
    onRelate: (from, to) => { $('relation-hint').hidden = true; commands.relate(from, to); },
    onToggle: id => commands.toggleCollapse(id),
    onKey: handleKey,
    onZoom: zoom => { $('zoom-value').textContent = `${Math.round(zoom * 100)}%`; },
    onPlayChange: playing => { $('play-hint').hidden = !playing; },
  });
  commands = new Commands(state, canvas, toast);
  inspector = new Inspector($('inspector'), state, commands);
  const saver = new AutoSaver(state, localDraft, $('save-status'));
  const help = new Help($<HTMLDialogElement>('help-dialog'), { copy: text => void copyText(text), openPaste });

  // ---------- 帳號與雲端儲存 ----------
  let cloud: CloudStorage | null = null;

  // 衝突：覆蓋（保留我的版本）或載入別處的新版。
  const resolveConflict = async (storage: CloudStorage): Promise<'overwrite' | 'reload'> => {
    if (confirm(t('conflict.ask'))) return 'overwrite';
    const { map, detail } = await loadMap(storage.id);
    storage.markLoaded(detail.updatedAt);
    state.replace(map, false);
    saver.reset();
    return 'reload';
  };

  const setUrlId = (id: string | null) => {
    const url = new URL(location.href);
    url.searchParams.delete('map');
    if (id) url.searchParams.set('id', id); else url.searchParams.delete('id');
    history.replaceState(null, '', url);
  };

  function useCloud(id: string, updatedAt: number): void {
    cloud = new CloudStorage(id, updatedAt, resolveConflict);
    storageSet(CURRENT_CLOUD_KEY, id);
    setUrlId(id);
    saver.reset(cloud);
  }

  async function openCloudMap(id: string): Promise<void> {
    await saver.flush();
    const { map, detail } = await loadMap(id);
    state.replace(map, false);
    useCloud(id, detail.updatedAt);
    canvas.fit();
  }

  // 把目前的圖存成帳號裡的新圖。
  async function uploadCurrent(): Promise<void> {
    const detail = await createMap(state.map);
    useCloud(detail.id, detail.updatedAt);
  }

  // 登入後：網址指定 > 上次開的圖 > 把目前畫面上的圖存成新圖。fresh 表示目前內容來自分享連結（另存一份）。
  async function attachCloud(preferredId: string | null, fresh: boolean): Promise<void> {
    for (const id of [preferredId, fresh ? null : storageGet(CURRENT_CLOUD_KEY)]) {
      if (!id) continue;
      try { await openCloudMap(id); return; } catch { /* 不存在或無權限，改試下一個 */ }
    }
    await uploadCurrent();
    toast(t('account.uploaded'));
  }

  // 新增心智圖：登入時直接建在帳號裡。
  async function newCloudMap(): Promise<void> {
    await saver.flush();
    state.replace(blankMap(t('default.central')), false);
    canvas.fit();
    try { await uploadCurrent(); } catch (error) { toast(errorMessage(error)); }
  }

  const account = new AccountUI($('account-slot'), $<HTMLDialogElement>('account-dialog'), $<HTMLDialogElement>('maps-dialog'), {
    // 剛登入：把目前畫面上的圖存到帳號（不覆蓋帳號裡既有的圖）。
    onSignedIn: async user => {
      try { await uploadCurrent(); toast(`${t('account.welcome', { email: user.email })} · ${t('account.uploaded')}`); }
      catch (error) { toast(errorMessage(error)); }
    },
    onSignedOut: () => {
      cloud = null;
      storageSet(CURRENT_CLOUD_KEY, null);
      setUrlId(null);
      saver.reset(localDraft);
      void localDraft.save(state.map);
    },
    openMap: id => openCloudMap(id).catch(error => toast(errorMessage(error))),
    newMap: newCloudMap,
    currentMapId: () => cloud?.id ?? null,
  });

  // 排版／主題選單。
  const structureSelect = $<HTMLSelectElement>('structure');
  const themeSelect = $<HTMLSelectElement>('theme');
  for (const structure of STRUCTURES) structureSelect.add(new Option(t(`structure.${structure}` as I18nKey), structure));
  for (const theme of THEME_NAMES) themeSelect.add(new Option(t(`theme.${theme}` as I18nKey), theme));
  structureSelect.addEventListener('change', () => { commands.run([{ op: 'set_map', structure: structureSelect.value as Structure }]); canvas.focus(); });
  themeSelect.addEventListener('change', () => { commands.run([{ op: 'set_map', theme: themeSelect.value }]); canvas.focus(); });

  // 狀態變動 → 重繪、面板、工具列、大綱、自動儲存。
  const sync = (reason: string) => {
    if (reason === 'select') canvas.drawOverlay(); else canvas.render();
    inspector.refresh();
    structureSelect.value = state.map.structure;
    themeSelect.value = state.map.theme;
    document.querySelectorAll<HTMLButtonElement>('[data-action="undo"]').forEach(button => { button.disabled = !state.canUndo; });
    document.querySelectorAll<HTMLButtonElement>('[data-action="redo"]').forEach(button => { button.disabled = !state.canRedo; });
    if (!$('outline-panel').hidden && document.activeElement !== outlineText) outlineText.value = serializeOutline(state.map, { ids: 'none' });
    document.title = `${state.map.root.title} · Sind`;
    if (reason !== 'select') saver.schedule();
  };
  state.subscribe(sync);
  canvas.render();
  sync('replace');
  $('loading').hidden = true;
  canvas.focus();

  // 已登入：改用雲端儲存。
  const params = new URLSearchParams(location.search);
  const user = await me();
  account.setUser(user);
  if (user) {
    try { await attachCloud(params.get('id'), params.has('map')); }
    catch (error) { toast(errorMessage(error)); }
  }

  // ---------- 快捷鍵（畫布有焦點且未在編輯文字時） ----------
  function handleKey(event: KeyboardEvent): boolean {
    const mod = isMac ? event.metaKey : event.ctrlKey;
    const key = event.key;
    if (mod && !event.shiftKey && key.toLowerCase() === 'z') { state.undo(); return true; }
    if (mod && ((event.shiftKey && key.toLowerCase() === 'z') || key.toLowerCase() === 'y')) { state.redo(); return true; }
    if (mod && key === 'Enter') { commands.insertParent(); return true; }
    if (mod && key === '/') { commands.toggleCollapse(); return true; }
    if (mod && key.toLowerCase() === 'b') { commands.toggleBoundary(); return true; }
    if (mod && key.toLowerCase() === 's') { void saver.flush(); return true; }
    if (mod && key === '0') { canvas.fit(); return true; }
    if (mod && (key === '=' || key === '+')) { canvas.zoomBy(1.2); return true; }
    if (mod && key === '-') { canvas.zoomBy(1 / 1.2); return true; }
    if (mod) return false; // 其餘組合鍵（複製、貼上等）交給瀏覽器事件
    if (key === 'F5') { playAnimation(); return true; }
    if (event.altKey && key === 'ArrowUp') { commands.reorder(-1); return true; }
    if (event.altKey && key === 'ArrowDown') { commands.reorder(1); return true; }
    if (event.altKey && key.toLowerCase() === 'r') { startRelation(); return true; }
    if (event.altKey) return false;
    switch (key) {
      case 'Tab': commands.addChild(); return true;
      case 'Enter': commands.addSibling(event.shiftKey); return true;
      case 'Delete': case 'Backspace': commands.deleteSelection(); return true;
      case 'F2': case ' ': {
        const selection = state.selection;
        if (selection?.kind === 'topic') canvas.startEdit(selection.id);
        return true;
      }
      case 'ArrowLeft': commands.navigate('left'); return true;
      case 'ArrowRight': commands.navigate('right'); return true;
      case 'ArrowUp': commands.navigate('up'); return true;
      case 'ArrowDown': commands.navigate('down'); return true;
      case 'Escape': state.select({ kind: 'topic', id: state.map.root.id }); return true;
      default: return false;
    }
  }

  // 剪貼簿：複製／剪下子樹為大綱文字；貼上大綱為子主題。
  canvas.input.addEventListener('copy', event => {
    if (canvas.isEditing) return;
    const text = commands.copyText();
    if (text) { event.clipboardData?.setData('text/plain', text); event.preventDefault(); toast(t('toast.copied')); }
  });
  canvas.input.addEventListener('cut', event => {
    if (canvas.isEditing) return;
    const text = commands.cut();
    if (text) { event.clipboardData?.setData('text/plain', text); event.preventDefault(); }
  });
  canvas.input.addEventListener('paste', event => {
    if (canvas.isEditing) return;
    const text = event.clipboardData?.getData('text/plain') ?? '';
    event.preventDefault();
    if (text.trim()) commands.paste(text);
  });

  // 播放建構動畫：用目前的圖產生動態 SVG，就地播放。
  function playAnimation(): void {
    canvas.play(exportSvgText(state.map, fonts, true));
  }

  function startRelation(): void {
    const selection = state.selection;
    if (selection?.kind !== 'topic') { toast(t('toast.nothingSelected')); return; }
    canvas.startRelation(selection.id);
    $('relation-hint').hidden = false;
  }

  // ---------- 貼上大綱視窗 ----------
  const pasteDialog = $<HTMLDialogElement>('paste-dialog');
  const pasteText = $<HTMLTextAreaElement>('paste-text');
  function openPaste(): void {
    pasteText.value = '';
    showDiagnostics($('paste-diagnostics'), []);
    pasteDialog.showModal();
    pasteText.focus();
  }
  $('paste-replace').addEventListener('click', () => {
    try {
      const { map, diagnostics } = outlineToMap(pasteText.value);
      state.replace(map);
      pasteDialog.close();
      canvas.fit();
      if (diagnostics.length) showImportNotes(diagnostics);
    } catch (error) {
      showDiagnostics($('paste-diagnostics'), (error as { diagnostics?: Diagnostic[] }).diagnostics ?? [{ line: 0, severity: 'error', message: t('error.parse') }]);
    }
  });
  $('paste-insert').addEventListener('click', () => {
    if (commands.paste(pasteText.value)) pasteDialog.close();
  });
  // 任何對話框關閉後把焦點還給畫布，快捷鍵與直接打字才會生效。
  for (const id of ['paste-dialog', 'help-dialog', 'account-dialog', 'maps-dialog', 'diag-dialog']) $(id).addEventListener('close', () => canvas.focus());

  function showImportNotes(diagnostics: Diagnostic[]): void {
    showDiagnostics($('diag-list'), diagnostics);
    $<HTMLDialogElement>('diag-dialog').showModal();
  }

  // ---------- 大綱面板 ----------
  let outlineTimer = 0;
  outlineText.addEventListener('input', () => {
    clearTimeout(outlineTimer);
    outlineTimer = window.setTimeout(() => {
      try {
        const { map, diagnostics } = outlineToMap(outlineText.value);
        showDiagnostics($('outline-diagnostics'), diagnostics);
        state.replace(map);
      } catch (error) {
        showDiagnostics($('outline-diagnostics'), (error as { diagnostics?: Diagnostic[] }).diagnostics ?? []);
      }
    }, 500);
  });
  function toggleOutline(): void {
    const panel = $('outline-panel');
    panel.hidden = !panel.hidden;
    if (!panel.hidden) outlineText.value = serializeOutline(state.map, { ids: 'none' });
  }

  // ---------- 檔案 ----------
  const fileInput = $<HTMLInputElement>('file-input');
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) return;
    try {
      const result = await importFile(file);
      state.replace(result.map);
      canvas.fit();
      toast(result.sheets > 1 ? t('toast.sheets', { n: result.sheets }) : t('toast.imported'));
      if (result.diagnostics.length) showImportNotes(result.diagnostics);
    } catch (error) {
      const diagnostics = (error as { diagnostics?: Diagnostic[] }).diagnostics;
      if (diagnostics) showImportNotes(diagnostics); else toast(`${t('error.file')}: ${(error as Error).message}`);
    }
  });

  const actions: Record<string, () => void | Promise<void>> = {
    new: async () => {
      if (account.user) { await newCloudMap(); return; }
      if (confirm(t('confirm.new'))) { state.replace(blankMap(t('default.central'))); canvas.fit(); }
    },
    maps: () => account.openMaps(),
    open: () => fileInput.click(),
    paste: openPaste,
    'export-png': async () => download(await exportPng(state.map, fonts), `${baseName(state.map)}.png`),
    'export-svg': () => download(new Blob([exportSvgText(state.map, fonts)], { type: 'image/svg+xml' }), `${baseName(state.map)}.svg`),
    'export-animated': () => download(new Blob([exportSvgText(state.map, fonts, true)], { type: 'image/svg+xml' }), `${baseName(state.map)}-animated.svg`),
    play: playAnimation,
    'copy-claude': () => copyText(claudeBrief(state.map, t('brief.intro'))),
    'export-md': () => download(exportMarkdown(state.map), `${baseName(state.map)}.md`),
    'export-xmind': () => download(exportXmindBlob(state.map), `${baseName(state.map)}.xmind`),
    share: async () => {
      // 登入時分享帳號裡的圖（連結永遠顯示最新內容）；未登入則建立匿名快照。
      try {
        if (cloud) await saver.flush();
        const url = cloud ? await shareCloudMap(cloud.id) : await share(state.map);
        await copyText(url);
        toast(`${t('toast.shared')}: ${url}`);
      } catch (error) { toast(errorMessage(error)); }
    },
    child: () => commands.addChild(),
    sibling: () => commands.addSibling(),
    parent: () => commands.insertParent(),
    relation: startRelation,
    boundary: () => commands.toggleBoundary(),
    note: () => inspector.focusNote(),
    outline: toggleOutline,
    fit: () => canvas.fit(),
    'zoom-in': () => canvas.zoomBy(1.2),
    'zoom-out': () => canvas.zoomBy(1 / 1.2),
    undo: () => state.undo(),
    redo: () => state.redo(),
    help: () => void help.open(),
  };

  document.addEventListener('click', event => {
    const target = (event.target as Element).closest<HTMLElement>('[data-action]');
    // 點選單外面時關閉所有選單。
    document.querySelectorAll<HTMLDetailsElement>('details.menu[open]').forEach(menu => { if (!menu.contains(event.target as Node) || target) menu.open = false; });
    if (!target) return;
    const action = actions[target.dataset.action!];
    if (!action) return;
    void Promise.resolve(action()).catch(error => { console.error(error); toast((error as Error).message); });
    if (!['help', 'paste', 'note', 'open', 'new'].includes(target.dataset.action!)) canvas.focus();
  });

  // 唯讀除錯介面（端對端測試用）：取大綱、主題在畫面上的位置。
  Object.assign(window, {
    sindEditor: {
      outline: () => serializeOutline(state.map, { ids: 'none' }),
      selectedTitle: () => state.selectedTopic?.title ?? null,
      rect(title: string) {
        const node = canvas.layout.nodes.find(item => item.topic.title === title);
        if (!node) return null;
        const box = viewport.getBoundingClientRect();
        const topLeft = canvas.toScreen({ x: node.x, y: node.y });
        return { x: box.left + topLeft.x, y: box.top + topLeft.y, w: node.w * canvas.zoom, h: node.h * canvas.zoom };
      },
    },
  });

  // 語言切換：存設定後重新載入（介面字串在啟動時決定）。先存檔，避免遺失修改。
  const langSelect = $<HTMLSelectElement>('lang');
  langSelect.value = lang;
  langSelect.addEventListener('change', async () => {
    await saver.flush();
    storageSet('sind-lang', langSelect.value);
    const url = new URL(location.href);
    url.searchParams.set('lang', langSelect.value);
    location.href = url.toString();
  });

  // 網址 #help 直接開啟說明（官網連結用）。
  if (location.hash === '#help') void help.open('claude');
  window.addEventListener('beforeunload', () => { void saver.flush(); });
}

void main();
